// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

const ExperimentsContext = createContext();
const EXPERIMENTS_STORAGE_KEY = 'experimentsEnabled_v2';

export const useExperimentsState = () => {
    const context = useContext(ExperimentsContext);
    if (!context) {
        throw new Error('useExperimentsState must be used within an ExperimentsProvider');
    }
    return context;
};

export const ExperimentsProvider = ({ children }) => {
    // Initialize from localStorage if available, otherwise default to true
    const [experimentsEnabled, setExperimentsEnabled] = useState(() => {
        try {
            const stored = localStorage.getItem(EXPERIMENTS_STORAGE_KEY);
            return stored !== null ? stored === 'true' : true;
        } catch (error) {
            console.warn('Failed to read experiments preference from localStorage:', error);
            return true;
        }
    });

    // View presets feature flag — defaults to true (enabled for all users)
    const [viewPresetsEnabled, setViewPresetsEnabled] = useState(() => {
        try {
            const stored = localStorage.getItem('viewPresetsEnabled');
            return stored !== null ? stored === 'true' : true;
        } catch (error) {
            console.warn('Failed to read view presets preference from localStorage:', error);
            return true;
        }
    });

    // Save to localStorage whenever the state changes
    useEffect(() => {
        try {
            localStorage.setItem(EXPERIMENTS_STORAGE_KEY, experimentsEnabled.toString());
        } catch (error) {
            console.warn('Failed to save experiments preference to localStorage:', error);
        }
    }, [experimentsEnabled]);

    useEffect(() => {
        try {
            localStorage.setItem('viewPresetsEnabled', viewPresetsEnabled.toString());
        } catch (error) {
            console.warn('Failed to save view presets preference to localStorage:', error);
        }
    }, [viewPresetsEnabled]);

    const toggleExperiments = useCallback((newValue) => {
        setExperimentsEnabled(newValue);
    }, []);

    const contextValue = useMemo(() => ({
        experimentsEnabled,
        toggleExperiments,
        viewPresetsEnabled
    }), [experimentsEnabled, toggleExperiments, viewPresetsEnabled]);

    return (
        <ExperimentsContext.Provider value={contextValue}>
            {children}
        </ExperimentsContext.Provider>
    );
};
